import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toJS } from 'mobx';
import { isEmpty } from '../../../../helpers/common';
import UserPreferenceSingleton from '../../../../helpers/userPreferenceSingleton';
import { DRAFT_MESSAGE_TYPE } from '../../../../global/constants';
import DraftMessageCacheService from '../../../../services/draftMessageCacheService';
import { captureException } from '../../../../services/logService';
import { CommentFilesInterface } from '../../../../interfaces/TaskInterface';
import { DraftMessage } from '../../../../mobx/interfaces/comment';
import { TiptapEditorRef } from '../../../../interfaces/tiptapInterface';

const DRAFT_SAVE_DELAY = 200;

interface UseDraftMessageParams {
  content: string;
  uploadedFiles: CommentFilesInterface[];
  currentProjectId?: string;
  isNavbarMessage: boolean;
  routeChatGroupId?: string;
  inlineRef: React.MutableRefObject<TiptapEditorRef | null>;
  setContent: (value: string) => void;
  setUploadedFiles: (files: CommentFilesInterface[]) => void;
  // True while the edit-message modal is open. We pause saving drafts while editing.
  isEditing: boolean;
}

interface UseDraftMessageReturn {
  draftContent: string;
  draftFiles: CommentFilesInterface[];
  loadDraft: () => Promise<void>;
  saveDraft: () => Promise<void>;
  clearDraft: () => Promise<void>;
}

export const useDraftMessage = ({
  content,
  uploadedFiles,
  currentProjectId,
  isNavbarMessage,
  routeChatGroupId,
  inlineRef,
  setContent,
  setUploadedFiles,
  isEditing
}: UseDraftMessageParams): UseDraftMessageReturn => {
  const { search } = useLocation();
  const [draftContent, setDraftContent] = useState<string>('');
  const [draftFiles, setDraftFiles] = useState<CommentFilesInterface[]>([]);
  const saveTimerRef = useRef<number | null>(null);
  const loadRequestRef = useRef(0);
  const isHydratingDraftRef = useRef(false);
  // Latest editing state, read inside the save effects.
  const isEditingRef = useRef(false);
  // Previous editing state, used to detect when the edit modal closes.
  const wasEditingRef = useRef(false);

  const chatGroupId = useMemo(() => {
    const chatGroupQuery = new URLSearchParams(search).get('chatGroup');
    if (isNavbarMessage) {
      return (
        chatGroupQuery || (UserPreferenceSingleton.getInstance().getLastMessageGroup() as string) || currentProjectId
      );
    }

    return routeChatGroupId;
  }, [currentProjectId, isNavbarMessage, routeChatGroupId, search]);

  const loadDraft = useCallback(async () => {
    if (!chatGroupId) return;

    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    isHydratingDraftRef.current = true;
    setDraftContent('');
    setDraftFiles([]);
    setUploadedFiles([]);

    try {
      const draftRaw = await DraftMessageCacheService.getInstance()?.getCurrentDraftMessage(chatGroupId);
      if (loadRequestRef.current !== requestId) return;

      const draftMessage: DraftMessage | null = (draftRaw as DraftMessage) ?? null;
      if (draftMessage) {
        const savedMessage = draftMessage.message || '';
        const savedFiles = draftMessage.file || [];

        if (inlineRef.current) {
          inlineRef.current.addContent(savedMessage);
        }

        setContent(savedMessage);
        setUploadedFiles(savedFiles);
        setDraftContent(savedMessage);
        setDraftFiles(savedFiles);
      }
    } catch (error) {
      captureException(error);
      // Draft loading is non-critical.
    } finally {
      if (loadRequestRef.current === requestId) {
        isHydratingDraftRef.current = false;
      }
    }
  }, [chatGroupId, inlineRef, setContent, setUploadedFiles]);

  const persistContentDraft = useCallback(async () => {
    if (!chatGroupId) return;

    const draftMessage = await DraftMessageCacheService.getInstance()?.getCurrentDraftMessage(chatGroupId);

    if (content !== '<p></p>' && !isEmpty(content)) {
      const message: DraftMessage = {
        id: chatGroupId,
        type: DRAFT_MESSAGE_TYPE.COMMENT,
        message: content,
        file: [...(draftMessage?.file || [])]
      };
      await DraftMessageCacheService.getInstance()?.addBulk([message]);
      setDraftContent(content);
      setDraftFiles(message.file || []);
      return;
    }

    if (content === '<p></p>') {
      if (isEmpty(draftMessage?.file)) {
        await DraftMessageCacheService.getInstance()?.removeBulk([chatGroupId]);
        setDraftContent('');
        setDraftFiles([]);
      }
    }
  }, [chatGroupId, content]);

  const persistFileDraft = useCallback(async () => {
    if (!chatGroupId) return;

    const draftMessage = await DraftMessageCacheService.getInstance()?.getCurrentDraftMessage(chatGroupId);
    const plainFiles = toJS(uploadedFiles);
    const message: DraftMessage = {
      id: chatGroupId,
      type: DRAFT_MESSAGE_TYPE.COMMENT,
      message: draftMessage?.message,
      file: plainFiles || []
    };

    await DraftMessageCacheService.getInstance()?.addBulk([message]);
    setDraftContent(message.message || '');
    setDraftFiles(plainFiles || []);

    if ((isEmpty(draftMessage?.message) || draftMessage?.message === '<p></p>') && isEmpty(uploadedFiles)) {
      await DraftMessageCacheService.getInstance()?.removeBulk([chatGroupId]);
      setDraftContent('');
      setDraftFiles([]);
    }
  }, [chatGroupId, uploadedFiles]);

  const saveDraft = useCallback(async () => {
    if (!chatGroupId) return;
    await persistContentDraft();
    await persistFileDraft();
  }, [chatGroupId, persistContentDraft, persistFileDraft]);

  const clearDraft = useCallback(async () => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    if (chatGroupId) {
      try {
        await DraftMessageCacheService.getInstance()?.removeBulk([chatGroupId]);
      } catch (error) {
        captureException(error);
        // ignore persistence cleanup failures
      }
    }

    setDraftContent('');
    setDraftFiles([]);
  }, [chatGroupId]);

  // Keep the editing flag ref up to date for the save effects below.
  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  // When the edit modal closes, reload the real draft so it shows in the composer again.
  useEffect(() => {
    if (wasEditingRef.current && !isEditing) {
      void loadDraft();
    }
    wasEditingRef.current = isEditing;
  }, [isEditing, loadDraft]);

  useEffect(() => {
    void loadDraft();
  }, [loadDraft]);

  useEffect(() => {
    if (isHydratingDraftRef.current || isEditingRef.current) return;
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = window.setTimeout(() => {
      void persistContentDraft();
    }, DRAFT_SAVE_DELAY);

    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, [persistContentDraft]);

  useEffect(() => {
    if (isHydratingDraftRef.current || isEditingRef.current) return;
    void persistFileDraft();
  }, [persistFileDraft]);

  return {
    draftContent,
    draftFiles,
    loadDraft,
    saveDraft,
    clearDraft
  };
};
